// Bespoke Ashen Host and Astral Dominion squad members, plus the Core Annex carriers and
// beasts of every faction. These procedural rigs replace the shared humanoid fallback with
// faction-readable silhouettes and keep one pose path for both the live renderer and Model
// Viewer. Local +z is forward.

import * as THREE from 'three';
import { OWNER_COLORS } from './unitStyles';

export const BESPOKE_ASHEN_SQUAD_DEFS = new Set([
  'u-ah-shade',
  'u-ah-reavers',
  'u-ah-ghoul-pack',
  'u-ah-bone-archers',
  'u-ah-corpse-carrier',
  'u-ah-flesh-guard',
  'u-ah-sacrificial-brute',
  'u-ah-soul-collector',
  'u-ah-gore-engine',
  'u-ah-soul-lanterns',
  'u-ah-wailing-brides',
  'u-ah-plague-apostles',
  'u-ah-ebon-executioners',
  'u-ah-cinder-revenants',
  'u-ah-grave-leviathan',
  'u-ah-deathless-legion',
  'u-ah-bone-hearse',
]);

export const BESPOKE_ASTRAL_SQUAD_DEFS = new Set([
  'u-ad-blink-infantry',
  'u-ad-arc-snipers',
  'u-ad-phase-tank',
  'u-ad-targeting-construct',
  'u-ad-shield-drone',
  'u-ad-phase-engineer',
  'u-ad-siege-orb',
  'u-ad-rift-hounds',
  'u-ad-prism-adepts',
  'u-ad-null-monks',
  'u-ad-vector-blades',
  'u-ad-gravity-weavers',
  'u-ad-celestial-wardens',
  'u-ad-solar-ark',
  'u-ad-prism-skiff',
]);

// Verdant annex creatures render through this dispatch (not verdantSquadRigs/beastRigs)
// deliberately: this renderer branch places members with memberOffsets, which centers a
// single-member squad on its sim position. The verdant and beast branches fall back to
// multi-member formation tables whose slot 0 is off-center — wrong for a lone treant.
export const BESPOKE_VERDANT_ANNEX_DEFS = new Set([
  'u-vw-rootwarden',
  'u-vw-seedback',
]);

export function isBespokeFactionSquad(defId: string): boolean {
  return (
    BESPOKE_ASHEN_SQUAD_DEFS.has(defId) ||
    BESPOKE_ASTRAL_SQUAD_DEFS.has(defId) ||
    BESPOKE_VERDANT_ANNEX_DEFS.has(defId)
  );
}

export interface FactionSquadPose {
  time: number;
  phase: number;
  moveAmt: number;
  strike: number;
  charge: number;
}

export interface FactionSquadRig {
  group: THREE.Group;
  pose: (frame: FactionSquadPose) => void;
}

interface Puppet {
  root: THREE.Group;
  body?: THREE.Object3D;
  head?: THREE.Object3D;
  legL?: THREE.Object3D;
  legR?: THREE.Object3D;
  armL?: THREE.Object3D;
  armR?: THREE.Object3D;
  weapon?: THREE.Object3D;
  orbitals: THREE.Object3D[];
  pulseMeshes: THREE.Mesh[];
  baseY: number;
  gait: 'walk' | 'crawl' | 'hover' | 'wheel' | 'serpent';
}

function mix(a: number, b: number, t: number): THREE.Color {
  return new THREE.Color(a).lerp(new THREE.Color(b), t);
}

function mat(color: number | THREE.Color, roughness = 0.82, metalness = 0.08): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: color instanceof THREE.Color ? color : new THREE.Color(color),
    roughness,
    metalness,
    flatShading: true,
  });
}

function glow(color: number, intensity = 1.1, opacity = 1): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: intensity,
    roughness: 0.28,
    transparent: opacity < 1,
    opacity,
    depthWrite: opacity >= 1,
    side: THREE.DoubleSide,
  });
}

function finish(group: THREE.Group, scale: number, shadowRadius: number, floating = false): void {
  const shadow = new THREE.Mesh(
    new THREE.CircleGeometry(shadowRadius, 18),
    new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: floating ? 0.12 : 0.2, depthWrite: false }),
  );
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = 0.012;
  group.add(shadow);
  group.scale.setScalar(scale);
  group.traverse((o) => {
    if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
}

function limb(name: string, material: THREE.Material, x: number, y: number, length: number, radius: number): THREE.Group {
  const pivot = new THREE.Group();
  pivot.name = name;
  pivot.position.set(x, y, 0);
  const upper = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.72, radius, length, 6), material);
  upper.position.y = -length / 2;
  pivot.add(upper);
  return pivot;
}

function blade(material: THREE.Material, length: number, width: number, hooked = false): THREE.Group {
  const weapon = new THREE.Group();
  const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.04, 0.3, 5), mat(0x241c1a, 0.95));
  grip.position.y = 0.05;
  weapon.add(grip);
  const edge = new THREE.Mesh(new THREE.BoxGeometry(width, length, 0.055), material);
  edge.position.set(hooked ? width * 0.32 : 0, length / 2 + 0.22, 0);
  edge.rotation.z = hooked ? -0.12 : 0;
  weapon.add(edge);
  const tip = new THREE.Mesh(new THREE.ConeGeometry(width * 0.72, width * 1.4, 4), material);
  tip.position.set(hooked ? width * 0.5 : 0, length + 0.25, 0);
  tip.rotation.y = Math.PI / 4;
  weapon.add(tip);
  return weapon;
}

function boneBow(material: THREE.Material): THREE.Group {
  const bow = new THREE.Group();
  for (const sy of [-1, 1]) {
    const rib = new THREE.Mesh(new THREE.TorusGeometry(0.48, 0.035, 5, 12, Math.PI * 0.72), material);
    rib.rotation.set(0, Math.PI / 2, sy > 0 ? 0.15 : Math.PI + 0.15);
    rib.position.y = sy * 0.25;
    bow.add(rib);
  }
  const string = new THREE.Mesh(new THREE.CylinderGeometry(0.009, 0.009, 1.05, 4), mat(0x1b1113));
  bow.add(string);
  return bow;
}

function ashenHumanoid(defId: string, owner: 0 | 1, variant: number): Puppet {
  const root = new THREE.Group();
  const bone = mat(variant % 2 ? 0xb7aa96 : 0xc9beaa, 0.94);
  const char = mat(mix(0x242127, OWNER_COLORS[owner], 0.08), 0.72, 0.35);
  const flesh = mat(variant % 2 ? 0x76504f : 0x865b57, 0.98);
  const cloth = mat(mix(0x241d27, OWNER_COLORS[owner], 0.1), 1);
  const rust = mat(0x673a2d, 0.76, 0.48);
  const soul = glow(0xc51732, 1.45);
  const large = defId === 'u-ah-flesh-guard' || defId === 'u-ah-sacrificial-brute';
  const spectral = defId === 'u-ah-shade' || defId === 'u-ah-wailing-brides';
  const robed = spectral || defId === 'u-ah-plague-apostles' || defId === 'u-ah-soul-collector';
  const armored = defId === 'u-ah-reavers' || defId === 'u-ah-ebon-executioners' || defId === 'u-ah-deathless-legion';

  let legL: THREE.Group | undefined;
  let legR: THREE.Group | undefined;
  if (robed) {
    const skirt = new THREE.Mesh(new THREE.ConeGeometry(large ? 0.48 : 0.34, 0.95, 7, 1, true), spectral ? glow(0x6e466f, 0.42, 0.58) : cloth);
    skirt.position.y = 0.5;
    skirt.rotation.y = variant * 0.4;
    root.add(skirt);
  } else {
    legL = limb('legL', large ? flesh : bone, -0.18, 0.72, 0.74, large ? 0.16 : 0.1);
    legR = limb('legR', large ? flesh : bone, 0.18, 0.72, 0.72 + (variant % 2) * 0.09, large ? 0.18 : 0.09);
    root.add(legL, legR);
  }

  const body = new THREE.Group();
  body.name = 'body';
  body.position.y = large ? 1.32 : 1.16;
  root.add(body);
  const torso = new THREE.Mesh(
    large ? new THREE.CapsuleGeometry(0.4, 0.68, 4, 8) : new THREE.CylinderGeometry(0.22, 0.3, 0.72, 7),
    large ? flesh : robed ? cloth : bone,
  );
  torso.rotation.z = spectral ? 0.08 : (variant % 3 - 1) * 0.035;
  body.add(torso);

  // Exposed rib hoops and a crimson soul make even armored bodies read as assembled dead.
  if (!large) {
    for (let i = 0; i < 3; i++) {
      const rib = new THREE.Mesh(new THREE.TorusGeometry(0.25 - i * 0.025, 0.025, 5, 10, Math.PI), bone);
      rib.rotation.x = Math.PI / 2;
      rib.position.set(0, 0.18 - i * 0.16, 0.18);
      body.add(rib);
    }
  }
  const soulCore = new THREE.Mesh(new THREE.OctahedronGeometry(large ? 0.13 : 0.09, 0), soul);
  soulCore.name = 'pulse';
  soulCore.position.set(0, 0.02, 0.24);
  body.add(soulCore);

  if (armored) {
    const chest = new THREE.Mesh(new THREE.BoxGeometry(large ? 0.85 : 0.58, large ? 0.65 : 0.48, 0.18), char);
    chest.position.set(0.04 * (variant % 2 ? 1 : -1), 0.04, 0.12);
    chest.rotation.z = (variant % 3 - 1) * 0.08;
    body.add(chest);
  } else if (large) {
    for (const sx of [-1, 1]) {
      const plate = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.5, 5), bone);
      plate.position.set(sx * 0.32, 0.16, 0.05);
      plate.rotation.z = sx * -0.8;
      body.add(plate);
    }
  }

  const head = new THREE.Group();
  head.name = 'head';
  head.position.set(0, large ? 1.94 : 1.65, 0.02);
  root.add(head);
  const skull = new THREE.Mesh(new THREE.SphereGeometry(large ? 0.24 : 0.2, 8, 6), large ? flesh : bone);
  skull.scale.set(0.88, 1.0, 0.9);
  head.add(skull);
  for (const sx of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(large ? 0.04 : 0.035, 5, 4), soul);
    eye.position.set(sx * (large ? 0.09 : 0.075), 0.025, large ? 0.2 : 0.17);
    head.add(eye);
  }
  if (armored) {
    const mask = new THREE.Mesh(new THREE.BoxGeometry(large ? 0.42 : 0.34, 0.28, 0.08), defId === 'u-ah-ebon-executioners' ? cloth : char);
    mask.position.set(0, -0.01, 0.18);
    mask.rotation.z = (variant % 3 - 1) * 0.04;
    head.add(mask);
    const slit = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.035, 0.02), soul);
    slit.position.set(0, 0.035, 0.23);
    head.add(slit);
  }
  if (defId === 'u-ah-plague-apostles') {
    const beak = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.42, 6), char);
    beak.rotation.x = Math.PI / 2;
    beak.position.set(0, -0.04, 0.3);
    head.add(beak);
    const hood = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.5, 7), cloth);
    hood.position.y = 0.12;
    head.add(hood);
  }
  if (defId === 'u-ah-wailing-brides') {
    const veil = new THREE.Mesh(new THREE.PlaneGeometry(0.7, 1.25), glow(0xbca0c9, 0.3, 0.32));
    veil.position.set(0, -0.35, -0.13);
    veil.rotation.x = 0.08;
    head.add(veil);
  }

  const armL = limb('armL', large ? flesh : armored ? char : bone, -0.38 - (large ? 0.16 : 0), large ? 1.58 : 1.38, large ? 0.88 : 0.7, large ? 0.18 : 0.085);
  const armR = limb('armR', large ? flesh : armored ? char : bone, 0.38 + (large ? 0.16 : 0), large ? 1.58 : 1.38, large ? 0.82 : 0.68, large ? 0.2 : 0.085);
  root.add(armL, armR);
  let weapon: THREE.Group | undefined;
  if (defId === 'u-ah-bone-archers') weapon = boneBow(bone);
  else if (defId === 'u-ah-plague-apostles' || defId === 'u-ah-soul-collector') {
    weapon = new THREE.Group();
    const staff = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.045, 1.55, 6), rust);
    staff.position.y = 0.58;
    weapon.add(staff);
    const cage = new THREE.Mesh(new THREE.TorusGeometry(0.2, 0.035, 5, 10), rust);
    cage.position.y = 1.35;
    weapon.add(cage);
    const spirit = new THREE.Mesh(new THREE.OctahedronGeometry(0.1), soul);
    spirit.position.y = 1.35;
    weapon.add(spirit);
  } else if (!spectral && defId !== 'u-ah-flesh-guard') {
    weapon = blade(defId === 'u-ah-cinder-revenants' ? soul : defId === 'u-ah-ebon-executioners' ? char : rust, large ? 1.0 : 0.82, large ? 0.2 : 0.13, defId === 'u-ah-reavers');
  }
  if (weapon) {
    weapon.name = 'weapon';
    weapon.position.set(0, -0.58, 0.15);
    weapon.rotation.x = 0.5;
    armL.add(weapon);
  }

  // Stitches, hooks, chains and ember cracks break the symmetry of every member.
  if (large) {
    for (let i = 0; i < 4; i++) {
      const stitch = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.025, 0.025), char);
      stitch.position.set((i % 2 ? 1 : -1) * 0.15, 1.15 + i * 0.16, 0.39);
      stitch.rotation.z = (i % 2 ? 1 : -1) * 0.35;
      root.add(stitch);
    }
    for (const sx of [-1, 1]) {
      const chain = new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.025, 5, 8), rust);
      chain.position.set(sx * 0.52, 1.38, 0.25);
      chain.rotation.y = Math.PI / 2;
      root.add(chain);
    }
  }
  if (defId === 'u-ah-cinder-revenants') {
    for (let i = 0; i < 5; i++) {
      const crack = new THREE.Mesh(new THREE.BoxGeometry(0.025, 0.25, 0.018), soul);
      crack.position.set((i - 2) * 0.09, 1.0 + (i % 2) * 0.22, 0.31);
      crack.rotation.z = (i - 2) * 0.18;
      root.add(crack);
    }
  }

  return { root, body, head, legL, legR, armL, armR, weapon, orbitals: [], pulseMeshes: [soulCore], baseY: spectral ? 0.42 : 0, gait: spectral ? 'hover' : 'walk' };
}

function ghoul(owner: 0 | 1, variant: number): Puppet {
  const root = new THREE.Group();
  const flesh = mat(mix(variant % 2 ? 0x6d5452 : 0x80605a, OWNER_COLORS[owner], 0.08), 1);
  const bone = mat(0xc0b39e, 0.95);
  const claw = mat(0x292329, 0.75, 0.28);
  const soul = glow(0xc51732, 1.35);
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.22, 0.58, 3, 7), flesh);
  body.position.set(0, 0.68, 0.05);
  body.rotation.x = Math.PI / 2.8;
  root.add(body);
  for (let i = 0; i < 3; i++) {
    const rib = new THREE.Mesh(new THREE.TorusGeometry(0.19, 0.022, 5, 9, Math.PI), bone);
    rib.position.set(0, 0.73 + i * 0.08, 0.27 + i * 0.04);
    rib.rotation.x = Math.PI / 2;
    root.add(rib);
  }
  const head = new THREE.Group();
  head.position.set(0, 0.72, 0.55);
  root.add(head);
  const skull = new THREE.Mesh(new THREE.SphereGeometry(0.18, 7, 5), flesh);
  head.add(skull);
  const eye = new THREE.Mesh(new THREE.SphereGeometry(0.035, 5, 4), soul);
  eye.position.set((variant % 2 ? 1 : -1) * 0.06, 0.04, 0.16);
  head.add(eye);
  const arms: THREE.Group[] = [];
  for (const sx of [-1, 1]) {
    const arm = limb(sx < 0 ? 'armL' : 'armR', flesh, sx * 0.25, 0.7, 0.75 + (variant % 3) * 0.06, 0.08);
    arm.rotation.x = -1.0;
    arm.rotation.z = sx * -0.18;
    const hand = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.1, 0.22), claw);
    hand.position.set(0, -0.78, 0.08);
    arm.add(hand);
    for (let c = -1; c <= 1; c++) {
      const talon = new THREE.Mesh(new THREE.ConeGeometry(0.025, 0.24, 4), claw);
      talon.rotation.x = Math.PI / 2;
      talon.position.set(c * 0.085, -0.79, 0.24);
      arm.add(talon);
    }
    root.add(arm);
    arms.push(arm);
  }
  const legL = limb('legL', flesh, -0.16, 0.46, 0.48, 0.09);
  const legR = limb('legR', flesh, 0.16, 0.46, 0.48, 0.09);
  legL.rotation.x = 0.75;
  legR.rotation.x = 0.75;
  root.add(legL, legR);
  return { root, body, head, legL, legR, armL: arms[0], armR: arms[1], orbitals: [], pulseMeshes: [eye], baseY: 0, gait: 'crawl' };
}

function ashenConstruct(defId: string, owner: 0 | 1, variant: number): Puppet {
  const root = new THREE.Group();
  const bone = mat(mix(0xafa28d, OWNER_COLORS[owner], 0.07), 0.95);
  const iron = mat(0x342a2d, 0.68, 0.55);
  const flesh = mat(0x704844, 0.98);
  const cloth = mat(0x241d23, 1);
  const soul = glow(0xc20f2f, 1.5);
  const orbitals: THREE.Object3D[] = [];
  const pulses: THREE.Mesh[] = [];

  if (defId === 'u-ah-soul-lanterns') {
    const hood = new THREE.Mesh(new THREE.ConeGeometry(0.38, 0.62, 7), cloth);
    hood.position.y = 1.65;
    root.add(hood);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.75, 5), iron);
      bar.position.set(Math.cos(a) * 0.27, 0.88, Math.sin(a) * 0.27);
      root.add(bar);
    }
    for (const y of [0.5, 1.26]) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.04, 6, 12), iron);
      ring.rotation.x = Math.PI / 2;
      ring.position.y = y;
      root.add(ring);
    }
    const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.2, 1), soul);
    core.position.y = 0.88;
    root.add(core);
    pulses.push(core);
    return { root, orbitals, pulseMeshes: pulses, baseY: 0.55, gait: 'hover' };
  }

  if (defId === 'u-ah-grave-leviathan') {
    for (let i = 0; i < 7; i++) {
      const segment = new THREE.Group();
      segment.name = `segment${i}`;
      segment.position.set(0, 0.32 + Math.sin(i * 0.7) * 0.16, (i - 3) * -0.34);
      const vertebra = new THREE.Mesh(new THREE.CylinderGeometry(0.22 + i * 0.015, 0.28 + i * 0.015, 0.34, 8), bone);
      vertebra.rotation.x = Math.PI / 2;
      segment.add(vertebra);
      const ribs = new THREE.Mesh(new THREE.TorusGeometry(0.38 + i * 0.015, 0.045, 5, 10, Math.PI * 1.35), bone);
      ribs.rotation.x = Math.PI / 2;
      ribs.rotation.z = Math.PI * 0.82;
      segment.add(ribs);
      root.add(segment);
      orbitals.push(segment);
    }
    const maw = new THREE.Mesh(new THREE.ConeGeometry(0.52, 0.85, 8, 1, true), bone);
    maw.rotation.x = Math.PI / 2;
    maw.position.set(0, 0.55, 1.35);
    root.add(maw);
    const throat = new THREE.Mesh(new THREE.IcosahedronGeometry(0.22, 0), soul);
    throat.position.set(0, 0.55, 1.12);
    root.add(throat);
    pulses.push(throat);
    return { root, orbitals, pulseMeshes: pulses, baseY: 0, gait: 'serpent' };
  }

  // Corpse Carrier and Gore Engine share a low, brutal chassis but retain unique payloads.
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.58, 1.15, 4, 9), flesh);
  body.name = 'body';
  body.rotation.x = Math.PI / 2;
  body.position.set(0, 0.8, 0);
  root.add(body);
  for (let i = 0; i < 5; i++) {
    const rib = new THREE.Mesh(new THREE.TorusGeometry(0.58, 0.065, 6, 12, Math.PI), bone);
    rib.rotation.set(Math.PI / 2, 0, Math.PI);
    rib.position.set(0, 0.82, -0.55 + i * 0.28);
    root.add(rib);
  }
  for (const sx of [-1, 1]) {
    for (const z of [-0.55, 0.55]) {
      const leg = limb(`leg${sx}${z}`, flesh, sx * 0.52, 0.72, 0.65, 0.16);
      leg.position.z = z;
      leg.rotation.z = sx * -0.18;
      root.add(leg);
      orbitals.push(leg);
    }
  }
  if (defId === 'u-ah-corpse-carrier') {
    const cage = new THREE.Group();
    cage.name = 'payload';
    cage.position.set(0, 1.42, -0.08);
    root.add(cage);
    for (const x of [-0.48, 0, 0.48]) {
      const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 1.15, 5), iron);
      bar.position.set(x, 0, 0);
      cage.add(bar);
    }
    for (const y of [-0.52, 0.52]) {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.07, 0.75), iron);
      rail.position.y = y;
      cage.add(rail);
    }
    for (let i = 0; i < 3; i++) {
      const corpse = new THREE.Mesh(new THREE.CapsuleGeometry(0.12, 0.38, 3, 5), i % 2 ? bone : cloth);
      corpse.rotation.z = (i - 1) * 0.6;
      corpse.position.set((i - 1) * 0.28, -0.08 + (i % 2) * 0.2, 0.1);
      cage.add(corpse);
    }
  } else {
    const barrel = new THREE.Group();
    barrel.name = 'weapon';
    barrel.position.set(0, 1.05, 0.25);
    root.add(barrel);
    const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.4, 1.65, 9), flesh);
    tube.rotation.x = Math.PI / 2;
    tube.position.z = 0.7;
    barrel.add(tube);
    const muzzle = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.08, 6, 12), bone);
    muzzle.position.z = 1.55;
    muzzle.rotation.x = Math.PI / 2;
    barrel.add(muzzle);
    const core = new THREE.Mesh(new THREE.SphereGeometry(0.22, 8, 6), soul);
    core.position.z = 1.5;
    barrel.add(core);
    pulses.push(core);
  }
  return { root, body, weapon: root.getObjectByName('weapon'), orbitals, pulseMeshes: pulses, baseY: 0, gait: 'crawl' };
}

function astralHumanoid(defId: string, owner: 0 | 1, variant: number): Puppet {
  const root = new THREE.Group();
  const white = mat(mix(0xe4e9f2, OWNER_COLORS[owner], 0.13), 0.32, 0.62);
  const dark = mat(mix(0x20283a, OWNER_COLORS[owner], 0.1), 0.42, 0.7);
  const crystal = glow(variant % 2 ? 0x795cff : 0x42c8ff, 1.35, 0.9);
  const black = mat(0x090c16, 0.3, 0.82);
  const robed = defId === 'u-ad-prism-adepts' || defId === 'u-ad-null-monks' || defId === 'u-ad-gravity-weavers';
  const broad = defId === 'u-ad-celestial-wardens';
  const body = new THREE.Group();
  body.name = 'body';
  body.position.y = broad ? 1.4 : 1.2;
  root.add(body);
  const torso = new THREE.Mesh(
    broad ? new THREE.BoxGeometry(0.84, 0.8, 0.5) : new THREE.CylinderGeometry(0.22, robed ? 0.38 : 0.3, 0.78, 6),
    defId === 'u-ad-null-monks' ? black : white,
  );
  torso.rotation.y = Math.PI / 4;
  body.add(torso);
  if (robed) {
    const robe = new THREE.Mesh(new THREE.ConeGeometry(0.42, 0.9, 6, 1, true), defId === 'u-ad-null-monks' ? black : dark);
    robe.position.y = -0.55;
    body.add(robe);
  }
  // Detached shoulder plates create the mathematically assembled silhouette.
  const orbitals: THREE.Object3D[] = [];
  for (const sx of [-1, 1]) {
    const plate = new THREE.Mesh(new THREE.OctahedronGeometry(broad ? 0.3 : 0.2, 0), white);
    plate.name = 'orbital';
    plate.position.set(sx * (broad ? 0.58 : 0.42), 0.22, 0);
    plate.scale.set(1.35, 0.55, 0.7);
    body.add(plate);
    orbitals.push(plate);
  }
  const core = new THREE.Mesh(new THREE.OctahedronGeometry(broad ? 0.17 : 0.11, 0), crystal);
  core.position.set(0, 0.02, 0.3);
  body.add(core);

  const head = new THREE.Group();
  head.name = 'head';
  head.position.set(0, broad ? 2.03 : 1.73, 0);
  root.add(head);
  const mask = new THREE.Mesh(
    defId === 'u-ad-null-monks' ? new THREE.CylinderGeometry(0.2, 0.2, 0.38, 8) : new THREE.OctahedronGeometry(broad ? 0.25 : 0.2, 1),
    defId === 'u-ad-null-monks' ? black : white,
  );
  mask.rotation.z = defId === 'u-ad-null-monks' ? Math.PI / 2 : 0;
  head.add(mask);
  const visor = new THREE.Mesh(new THREE.BoxGeometry(broad ? 0.31 : 0.25, 0.045, 0.035), crystal);
  visor.position.set(0, 0.015, broad ? 0.23 : 0.19);
  head.add(visor);

  let legL: THREE.Group | undefined;
  let legR: THREE.Group | undefined;
  if (!robed) {
    legL = limb('legL', dark, -0.18, 0.8, broad ? 0.82 : 0.72, broad ? 0.15 : 0.1);
    legR = limb('legR', dark, 0.18, 0.8, broad ? 0.82 : 0.72, broad ? 0.15 : 0.1);
    root.add(legL, legR);
  }
  const armL = limb('armL', dark, broad ? -0.62 : -0.39, broad ? 1.6 : 1.42, broad ? 0.78 : 0.66, broad ? 0.16 : 0.09);
  const armR = limb('armR', dark, broad ? 0.62 : 0.39, broad ? 1.6 : 1.42, broad ? 0.78 : 0.66, broad ? 0.16 : 0.09);
  root.add(armL, armR);

  let weapon: THREE.Group | undefined;
  if (defId === 'u-ad-arc-snipers') {
    weapon = new THREE.Group();
    const railA = new THREE.Mesh(new THREE.BoxGeometry(0.05, 1.55, 0.07), dark);
    railA.position.x = -0.1;
    weapon.add(railA);
    const railB = railA.clone();
    railB.position.x = 0.1;
    weapon.add(railB);
    const prism = new THREE.Mesh(new THREE.OctahedronGeometry(0.12), crystal);
    prism.position.y = 0.55;
    weapon.add(prism);
  } else if (defId === 'u-ad-vector-blades') {
    weapon = blade(crystal, 1.05, 0.09);
    const second = blade(crystal, 0.9, 0.08);
    second.position.x = 0.18;
    armR.add(second);
  } else if (defId === 'u-ad-blink-infantry') {
    weapon = blade(crystal, 0.72, 0.1);
  } else if (defId === 'u-ad-phase-engineer') {
    weapon = new THREE.Group();
    for (let i = 0; i < 3; i++) {
      const tool = new THREE.Mesh(new THREE.OctahedronGeometry(0.09, 0), crystal);
      tool.position.set(Math.cos(i * 2.09) * 0.28, 0.5 + Math.sin(i * 2.09) * 0.28, 0);
      weapon.add(tool);
      orbitals.push(tool);
    }
  }
  if (weapon) {
    weapon.name = 'weapon';
    weapon.position.set(0, -0.55, 0.16);
    weapon.rotation.x = defId === 'u-ad-arc-snipers' ? 1.1 : 0.48;
    armL.add(weapon);
  }

  if (defId === 'u-ad-celestial-wardens') {
    const shield = new THREE.Group();
    shield.name = 'shield';
    shield.position.set(0.72, 1.12, 0.34);
    root.add(shield);
    const mirror = new THREE.Mesh(new THREE.CylinderGeometry(0.58, 0.58, 0.1, 20), white);
    mirror.rotation.z = Math.PI / 2;
    shield.add(mirror);
    const face = new THREE.Mesh(new THREE.CircleGeometry(0.5, 20), glow(0x8fdcff, 0.55, 0.55));
    face.rotation.y = Math.PI / 2;
    face.position.x = -0.06;
    shield.add(face);
    orbitals.push(shield);
  }

  // Null halo, prism satellites, or gravity weights communicate each caster's job.
  if (robed) {
    const count = defId === 'u-ad-null-monks' ? 3 : 4;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2;
      const geo = defId === 'u-ad-null-monks'
        ? new THREE.TorusGeometry(0.22 + i * 0.04, 0.025, 6, 14)
        : defId === 'u-ad-gravity-weavers'
          ? new THREE.BoxGeometry(0.18, 0.18, 0.18)
          : new THREE.OctahedronGeometry(0.14, 0);
      const orb = new THREE.Mesh(geo, defId === 'u-ad-null-monks' ? black : crystal);
      orb.name = 'orbital';
      orb.position.set(Math.cos(a) * 0.62, 1.45 + Math.sin(a) * 0.28, -0.12);
      root.add(orb);
      orbitals.push(orb);
    }
  }
  return { root, body, head, legL, legR, armL, armR, weapon, orbitals, pulseMeshes: [core, visor], baseY: robed ? 0.35 : 0, gait: robed ? 'hover' : 'walk' };
}

function astralConstruct(defId: string, owner: 0 | 1, variant: number): Puppet {
  const root = new THREE.Group();
  const shell = mat(mix(0xdde6f2, OWNER_COLORS[owner], 0.15), 0.3, 0.68);
  const dark = mat(0x182038, 0.38, 0.74);
  const energy = glow(variant % 2 ? 0x7b61ff : 0x43ceff, 1.5, 0.9);
  const voidMat = glow(0x25105b, 1.1, 0.9);
  const orbitals: THREE.Object3D[] = [];
  const pulses: THREE.Mesh[] = [];

  if (defId === 'u-ad-rift-hounds') {
    const body = new THREE.Mesh(new THREE.OctahedronGeometry(0.55, 1), shell);
    body.name = 'body';
    body.position.set(0, 0.72, 0);
    body.scale.set(0.72, 0.62, 1.35);
    root.add(body);
    const core = new THREE.Mesh(new THREE.OctahedronGeometry(0.3, 0), energy);
    core.position.set(0, 0.72, 0.03);
    root.add(core);
    pulses.push(core);
    const head = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.75, 4), shell);
    head.name = 'head';
    head.rotation.x = Math.PI / 2;
    head.position.set(0, 0.76, 0.86);
    root.add(head);
    for (const [name, x, z] of [['legFL', -0.34, 0.42], ['legFR', 0.34, 0.42], ['legBL', -0.34, -0.42], ['legBR', 0.34, -0.42]] as const) {
      const leg = limb(name, dark, x, 0.65, 0.68, 0.08);
      leg.position.z = z;
      const bladeLeg = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.45, 4), energy);
      bladeLeg.position.y = -0.7;
      leg.add(bladeLeg);
      root.add(leg);
      orbitals.push(leg);
    }
    return { root, body, head, orbitals, pulseMeshes: pulses, baseY: 0, gait: 'crawl' };
  }

  if (defId === 'u-ad-targeting-construct') {
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2;
      const leg = limb(`leg${i}`, dark, Math.cos(a) * 0.42, 1.0, 1.0, 0.09);
      leg.position.z = Math.sin(a) * 0.42;
      leg.rotation.z = Math.cos(a) * -0.28;
      root.add(leg);
      orbitals.push(leg);
    }
    const head = new THREE.Group();
    head.name = 'head';
    head.position.y = 1.45;
    root.add(head);
    const prism = new THREE.Mesh(new THREE.OctahedronGeometry(0.42, 0), shell);
    prism.scale.set(1, 0.75, 1);
    head.add(prism);
    const lens = new THREE.Mesh(new THREE.OctahedronGeometry(0.18, 0), glow(0xff315f, 1.6));
    lens.position.z = 0.38;
    head.add(lens);
    pulses.push(lens);
    for (let i = 0; i < 3; i++) {
      const reticle = new THREE.Mesh(new THREE.TorusGeometry(0.44 + i * 0.11, 0.018, 5, 20), glow(0xff315f, 0.8, 0.55));
      reticle.position.set(0, 1.45, 0.52 + i * 0.04);
      root.add(reticle);
      orbitals.push(reticle);
    }
    return { root, head, orbitals, pulseMeshes: pulses, baseY: 0, gait: 'walk' };
  }

  if (defId === 'u-ad-phase-tank') {
    const body = new THREE.Mesh(new THREE.BoxGeometry(1.75, 0.48, 1.25), shell);
    body.name = 'body';
    body.position.y = 0.75;
    body.geometry.rotateY(Math.PI / 4);
    root.add(body);
    const turret = new THREE.Group();
    turret.name = 'head';
    turret.position.set(0, 1.05, 0);
    root.add(turret);
    for (const sx of [-1, 1]) {
      const hull = new THREE.Mesh(new THREE.OctahedronGeometry(0.52, 0), shell);
      hull.position.x = sx * 0.42;
      hull.scale.set(1.25, 0.45, 1.45);
      turret.add(hull);
      orbitals.push(hull);
    }
    const cannon = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.14, 1.65, 8), dark);
    cannon.name = 'weapon';
    cannon.rotation.x = Math.PI / 2;
    cannon.position.z = 0.83;
    turret.add(cannon);
    const core = new THREE.Mesh(new THREE.OctahedronGeometry(0.2, 0), energy);
    core.position.set(0, 1.0, 0.48);
    root.add(core);
    pulses.push(core);
    return { root, body, head: turret, weapon: cannon, orbitals, pulseMeshes: pulses, baseY: 0.3, gait: 'hover' };
  }

  const huge = defId === 'u-ad-solar-ark';
  const isOrb = defId === 'u-ad-siege-orb';
  const isDrone = defId === 'u-ad-shield-drone';
  const radius = huge ? 0.82 : isOrb ? 0.58 : 0.34;
  const core = new THREE.Mesh(new THREE.IcosahedronGeometry(radius * 0.62, 1), huge ? glow(0xffd45a, 1.8) : isOrb ? voidMat : energy);
  core.position.y = huge ? 1.35 : 1.0;
  root.add(core);
  pulses.push(core);
  const ringCount = huge ? 4 : isOrb ? 3 : 2;
  for (let i = 0; i < ringCount; i++) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(radius + i * (huge ? 0.22 : 0.13), huge ? 0.07 : 0.045, 7, huge ? 28 : 18), i % 2 ? dark : shell);
    ring.name = 'orbital';
    ring.position.y = core.position.y;
    ring.rotation.set(i % 2 ? Math.PI / 2 : Math.PI / 5, i * 0.75, i * 0.45);
    root.add(ring);
    orbitals.push(ring);
  }
  if (isDrone) {
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2;
      const plate = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.58, 3), shell);
      plate.position.set(Math.cos(a) * 0.48, 1.0, Math.sin(a) * 0.48);
      plate.rotation.set(Math.PI / 2, 0, -a);
      root.add(plate);
      orbitals.push(plate);
    }
  }
  if (huge) {
    const prow = new THREE.Mesh(new THREE.ConeGeometry(0.35, 1.7, 4), shell);
    prow.rotation.x = Math.PI / 2;
    prow.position.set(0, 1.35, 1.25);
    root.add(prow);
    const bay = new THREE.Mesh(new THREE.BoxGeometry(1.35, 0.28, 0.85), dark);
    bay.position.set(0, 0.9, -0.55);
    root.add(bay);
  }
  return { root, orbitals, pulseMeshes: pulses, baseY: huge ? 0.75 : 0.55, gait: 'hover' };
}

// ---------------------------------------------------------------- Core Annex bespoke rigs
// Each builder returns a fully wired FactionSquadRig with its own pose closure (these
// vehicles/beasts don't fit the shared Puppet vocabulary). Local +z is forward.

function wrapAnnexRig(
  root: THREE.Group,
  uiScale: number,
  shadowRadius: number,
  floating: boolean,
  pose: (frame: FactionSquadPose) => void,
): FactionSquadRig {
  finish(root, uiScale, shadowRadius, floating);
  const group = new THREE.Group();
  group.add(root);
  return { group, pose };
}

/** Quadruped leg pivot with root-claw toes; origin at the hip so rotation.x strides. */
function clawLeg(name: string, x: number, y: number, z: number, len: number, r: number, hide: THREE.Material, claw: THREE.Material): THREE.Group {
  const pivot = limb(name, hide, x, y, len, r);
  pivot.position.z = z;
  for (const cx of [-0.6, 0, 0.6]) {
    const toe = new THREE.Mesh(new THREE.ConeGeometry(r * 0.45, r * 1.8, 5), claw);
    toe.rotation.x = Math.PI / 2 + cx * 0.4;
    toe.rotation.y = cx * 0.5;
    toe.position.set(cx * r * 0.9, -len + r * 0.3, r * 0.8);
    pivot.add(toe);
  }
  return pivot;
}

/**
 * Rootwarden Treant: walking tree-guardian. Bark-plated torso, moss shoulders, branch
 * antlers, glowing green eyes, root-claw feet, small flowers. The hidden `rootSkirt`
 * child (gnarled root ring + thorn tendrils + thorn-launcher buds) is toggled by the
 * renderer while the treant is rooted — it ships `visible = false`.
 */
function rootwardenTreant(owner: 0 | 1, uiScale: number): FactionSquadRig {
  const root = new THREE.Group();
  const bark = mat(mix(0x6a5236, OWNER_COLORS[owner], 0.08), 0.98, 0);
  const barkDark = mat(0x47351f, 1, 0);
  const moss = mat(mix(0x5f8a3c, OWNER_COLORS[owner], 0.1), 0.98, 0);
  const thorn = mat(0x9a7446, 0.9, 0.02);
  const eyeGlow = glow(0x8fe05a, 1.5);
  const petalPink = mat(0xe8a7c0, 0.85, 0);
  const petalGold = mat(0xf0cf6a, 0.8, 0);

  // legs: thick tapered trunks ending in splayed root claws
  const legL = clawLeg('legL', -0.3, 1.0, 0, 0.98, 0.17, barkDark, thorn);
  const legR = clawLeg('legR', 0.3, 1.0, 0, 0.98, 0.17, barkDark, thorn);
  root.add(legL, legR);

  // torso: bark-plated trunk with a moss shelf on each shoulder
  const body = new THREE.Group();
  body.name = 'body';
  body.position.y = 1.62;
  root.add(body);
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.56, 1.3, 8), bark);
  body.add(trunk);
  for (let i = 0; i < 5; i++) {
    const plate = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.4, 0.1), barkDark);
    plate.position.set((i % 2 ? 1 : -1) * (0.16 + (i % 3) * 0.07), 0.42 - i * 0.22, 0.44 - (i % 3) * 0.03);
    plate.rotation.z = (i % 2 ? 1 : -1) * 0.18;
    body.add(plate);
  }
  for (const sx of [-1, 1]) {
    const shoulderMoss = new THREE.Mesh(new THREE.IcosahedronGeometry(0.34, 0), moss);
    shoulderMoss.position.set(sx * 0.5, 0.68, -0.02);
    shoulderMoss.scale.set(1.15, 0.7, 1.0);
    body.add(shoulderMoss);
  }
  // small flowers blooming out of the moss
  for (const [fx, fy, fz, m] of [
    [-0.55, 0.86, 0.1, petalPink],
    [0.48, 0.9, -0.08, petalGold],
    [0.1, 0.55, 0.48, petalPink],
  ] as const) {
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.03, 0.16, 5), moss);
    stem.position.set(fx, fy, fz);
    body.add(stem);
    for (let p = 0; p < 4; p++) {
      const a = (p / 4) * Math.PI * 2;
      const petal = new THREE.Mesh(new THREE.SphereGeometry(0.045, 5, 4), m);
      petal.position.set(fx + Math.cos(a) * 0.055, fy + 0.1, fz + Math.sin(a) * 0.055);
      petal.scale.set(1, 0.5, 1);
      body.add(petal);
    }
    const heart = new THREE.Mesh(new THREE.SphereGeometry(0.03, 5, 4), petalGold);
    heart.position.set(fx, fy + 0.12, fz);
    body.add(heart);
  }

  // head: bark face, luminous green eyes, branch antlers
  const head = new THREE.Group();
  head.name = 'head';
  head.position.set(0, 2.52, 0.04);
  root.add(head);
  const face = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.5, 0.4), barkDark);
  head.add(face);
  const brow = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.1, 0.14), bark);
  brow.position.set(0, 0.14, 0.18);
  head.add(brow);
  for (const sx of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 5), eyeGlow);
    eye.position.set(sx * 0.11, 0.04, 0.21);
    head.add(eye);
    const antlerLow = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.62, 5), bark);
    antlerLow.position.set(sx * 0.26, 0.48, -0.04);
    antlerLow.rotation.z = sx * -0.55;
    head.add(antlerLow);
    const antlerHigh = new THREE.Mesh(new THREE.ConeGeometry(0.038, 0.42, 5), bark);
    antlerHigh.position.set(sx * 0.44, 0.72, -0.04);
    antlerHigh.rotation.z = sx * -0.95;
    head.add(antlerHigh);
    const antlerBud = new THREE.Mesh(new THREE.SphereGeometry(0.045, 5, 4), petalPink);
    antlerBud.position.set(sx * 0.34, 0.72, -0.04);
    head.add(antlerBud);
  }
  const crownMoss = new THREE.Mesh(new THREE.IcosahedronGeometry(0.2, 0), moss);
  crownMoss.position.set(0, 0.34, -0.08);
  crownMoss.scale.set(1.2, 0.6, 1.0);
  head.add(crownMoss);

  // arms: long bark limbs with claw fingers
  const armL = limb('armL', bark, -0.62, 2.18, 1.05, 0.13);
  const armR = limb('armR', bark, 0.62, 2.18, 1.05, 0.13);
  for (const arm of [armL, armR]) {
    for (const cx of [-0.07, 0, 0.07]) {
      const fingerClaw = new THREE.Mesh(new THREE.ConeGeometry(0.035, 0.3, 5), thorn);
      fingerClaw.position.set(cx, -1.16, 0.03);
      fingerClaw.rotation.x = Math.PI;
      arm.add(fingerClaw);
    }
    root.add(arm);
  }

  // rooted-state skirt: gnarled root ring + thorn tendrils + raised thorn-launcher buds.
  // Hidden by default — the renderer toggles `rootSkirt` visibility while rooted.
  const rootSkirt = new THREE.Group();
  rootSkirt.name = 'rootSkirt';
  rootSkirt.visible = false;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + 0.3;
    const spoke = new THREE.Group();
    spoke.rotation.y = -a;
    const gnarl = new THREE.Mesh(new THREE.ConeGeometry(0.15, 1.05, 5), i % 2 ? barkDark : bark);
    gnarl.rotation.x = Math.PI / 2 - 0.14; // lying outward, tip slightly buried
    gnarl.position.set(0, 0.13, 0.95);
    spoke.add(gnarl);
    const knuckle = new THREE.Mesh(new THREE.SphereGeometry(0.11, 5, 4), barkDark);
    knuckle.position.set(0, 0.16, 0.55);
    spoke.add(knuckle);
    rootSkirt.add(spoke);
  }
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.9;
    const tendril = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.72, 5), thorn);
    tendril.position.set(Math.cos(a) * 1.0, 0.32, Math.sin(a) * 1.0);
    tendril.rotation.set(Math.sin(a) * 0.85, 0, -Math.cos(a) * 0.85);
    rootSkirt.add(tendril);
  }
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.5;
    const stalk = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.075, 0.6, 5), barkDark);
    stalk.position.set(Math.cos(a) * 0.72, 0.3, Math.sin(a) * 0.72);
    stalk.rotation.set(Math.sin(a) * 0.3, 0, -Math.cos(a) * 0.3);
    rootSkirt.add(stalk);
    const bud = new THREE.Mesh(new THREE.IcosahedronGeometry(0.13, 0), moss);
    bud.position.set(Math.cos(a) * 0.88, 0.62, Math.sin(a) * 0.88);
    rootSkirt.add(bud);
    const spine = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.26, 5), eyeGlow);
    spine.position.set(Math.cos(a) * 0.93, 0.82, Math.sin(a) * 0.93);
    spine.rotation.set(Math.sin(a) * 0.35, 0, -Math.cos(a) * 0.35);
    rootSkirt.add(spine);
  }
  root.add(rootSkirt);

  return wrapAnnexRig(root, uiScale, 0.95, false, ({ time, phase, moveAmt, strike }) => {
    // heavy deliberate stride; at moveAmt 0 the legs go columnar so it reads planted
    const stride = Math.sin(phase) * 0.42 * moveAmt;
    legL.rotation.x = stride;
    legR.rotation.x = -stride;
    const idle = 1 - moveAmt;
    armL.rotation.x = -stride * 0.5 - strike * 1.05 + Math.sin(time * 0.9) * 0.045 * idle;
    armR.rotation.x = stride * 0.5 - strike * 0.4 - Math.sin(time * 0.9) * 0.045 * idle;
    body.rotation.z = Math.sin(phase) * 0.03 * moveAmt + Math.sin(time * 0.62) * 0.014 * idle;
    body.scale.y = 1 + Math.sin(time * 1.15) * 0.018 * idle; // slow woody breathing
    head.rotation.y = Math.sin(time * 0.48) * 0.16 * idle;
    head.rotation.x = strike * 0.22 - 0.04;
    crownMoss.rotation.y = Math.sin(time * 0.8) * 0.08;
    eyeGlow.emissiveIntensity = 1.5 + Math.sin(time * 2.2) * 0.25 + strike * 1.1;
  });
}

/**
 * Seedback Carrier: slow tortoise/beetle walker hauling a massive pointed seed pod
 * lashed on with vines, tiny sprouts growing from the pod.
 */
function seedbackCarrier(owner: 0 | 1, uiScale: number): FactionSquadRig {
  const root = new THREE.Group();
  const hide = mat(mix(0x7a6a44, OWNER_COLORS[owner], 0.08), 0.98, 0);
  const hideDark = mat(0x4e422a, 1, 0);
  const shellMoss = mat(mix(0x5c8140, OWNER_COLORS[owner], 0.1), 0.98, 0);
  const pod = mat(0xa9814a, 0.9, 0.02);
  const podDark = mat(0x7c5a32, 0.95, 0);
  const vine = mat(mix(0x4f7a3a, OWNER_COLORS[owner], 0.12), 0.92, 0);
  const sprout = mat(0x8fc061, 0.9, 0);
  const eye = mat(0x241c12, 1, 0);

  // low broad body under a mossy dome shell
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.55, 0.95, 5, 10), hide);
  body.name = 'body';
  body.rotation.x = Math.PI / 2;
  body.position.set(0, 0.82, 0);
  body.scale.set(1.15, 1, 0.75);
  root.add(body);
  const shell = new THREE.Mesh(new THREE.SphereGeometry(0.92, 10, 7, 0, Math.PI * 2, 0, Math.PI / 2), shellMoss);
  shell.position.y = 0.88;
  shell.scale.set(1.0, 0.62, 1.2);
  root.add(shell);
  const rim = new THREE.Mesh(new THREE.TorusGeometry(0.92, 0.07, 6, 16), hideDark);
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 0.9;
  rim.scale.set(1.0, 1.2, 1);
  root.add(rim);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.4;
    const scute = new THREE.Mesh(new THREE.IcosahedronGeometry(0.16, 0), i % 2 ? shellMoss : hideDark);
    scute.position.set(Math.cos(a) * 0.62, 1.18, Math.sin(a) * 0.74);
    scute.scale.set(1.2, 0.5, 1.2);
    root.add(scute);
  }

  // stumpy legs
  const legs = [
    clawLeg('legFL', -0.58, 0.72, 0.5, 0.66, 0.16, hide, hideDark),
    clawLeg('legFR', 0.58, 0.72, 0.5, 0.66, 0.16, hide, hideDark),
    clawLeg('legBL', -0.58, 0.72, -0.55, 0.66, 0.16, hide, hideDark),
    clawLeg('legBR', 0.58, 0.72, -0.55, 0.66, 0.16, hide, hideDark),
  ];
  for (const leg of legs) root.add(leg);

  // sleepy head on a slow neck
  const head = new THREE.Group();
  head.name = 'head';
  head.position.set(0, 0.85, 1.02);
  root.add(head);
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.22, 0.45, 7), hide);
  neck.rotation.x = 1.1;
  neck.position.set(0, 0.02, -0.05);
  head.add(neck);
  const skull = new THREE.Mesh(new THREE.SphereGeometry(0.24, 8, 6), hide);
  skull.position.set(0, 0.14, 0.22);
  skull.scale.set(0.9, 0.82, 1.1);
  head.add(skull);
  for (const sx of [-1, 1]) {
    const e = new THREE.Mesh(new THREE.SphereGeometry(0.04, 5, 4), eye);
    e.position.set(sx * 0.13, 0.2, 0.36);
    head.add(e);
    const lid = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.03, 0.06), hideDark);
    lid.position.set(sx * 0.13, 0.24, 0.37);
    head.add(lid);
  }

  // the cargo: a MASSIVE pointed seed pod strapped down with vines
  const podPivot = new THREE.Group();
  podPivot.name = 'pod';
  podPivot.position.set(0, 1.35, -0.12);
  podPivot.rotation.x = -0.12; // tip leans back
  root.add(podPivot);
  const podBase = new THREE.Mesh(new THREE.SphereGeometry(0.58, 9, 7), pod);
  podBase.position.y = 0.3;
  podBase.scale.set(1, 0.85, 1);
  podPivot.add(podBase);
  const podTip = new THREE.Mesh(new THREE.ConeGeometry(0.52, 1.5, 9), pod);
  podTip.position.y = 1.2;
  podPivot.add(podTip);
  for (let i = 0; i < 4; i++) {
    const seam = new THREE.Mesh(new THREE.BoxGeometry(0.05, 1.15, 0.05), podDark);
    const a = (i / 4) * Math.PI * 2 + 0.35;
    seam.position.set(Math.cos(a) * 0.34, 0.95, Math.sin(a) * 0.34);
    seam.rotation.z = Math.cos(a) * 0.3;
    seam.rotation.x = -Math.sin(a) * 0.3;
    podPivot.add(seam);
  }
  // vine lashings over the pod down to the shell
  for (const rz of [0.5, -0.55]) {
    const strap = new THREE.Mesh(new THREE.TorusGeometry(0.78, 0.05, 5, 14, Math.PI), vine);
    strap.rotation.set(0, Math.PI / 2, 0);
    strap.position.set(0, 0.35, rz);
    podPivot.add(strap);
  }
  const girth = new THREE.Mesh(new THREE.TorusGeometry(0.62, 0.045, 5, 14), vine);
  girth.rotation.x = Math.PI / 2;
  girth.position.y = 0.62;
  podPivot.add(girth);
  // tiny sprouts already growing out of the seed
  const sprouts: THREE.Mesh[] = [];
  for (const [sx, sy, sz] of [
    [0.3, 0.75, 0.3],
    [-0.34, 1.05, 0.12],
    [0.12, 1.5, -0.2],
  ] as const) {
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.028, 0.22, 4), sprout);
    stem.position.set(sx, sy, sz);
    stem.rotation.set(sz * 0.8, 0, -sx * 0.8);
    podPivot.add(stem);
    const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.07, 5, 4), sprout);
    leaf.position.set(sx * 1.25, sy + 0.12, sz * 1.25);
    leaf.scale.set(1, 0.4, 1.6);
    podPivot.add(leaf);
    sprouts.push(leaf);
  }

  return wrapAnnexRig(root, uiScale, 1.15, false, ({ time, phase, moveAmt, strike }) => {
    // ponderous diagonal gait — this thing is basically a walking granary
    const pace = 0.03 + moveAmt * 0.4;
    const diagonal = Math.sin(phase) * pace;
    legs[0].rotation.x = diagonal;
    legs[3].rotation.x = diagonal;
    legs[1].rotation.x = -diagonal;
    legs[2].rotation.x = -diagonal;
    const idle = 1 - moveAmt;
    head.rotation.y = Math.sin(time * 0.42) * 0.2 * idle;
    head.rotation.x = Math.sin(time * 0.75) * 0.05 * idle - strike * 0.15;
    head.position.y = 0.85 + Math.sin(time * 0.9) * 0.02 * idle;
    podPivot.rotation.z = Math.sin(phase) * 0.045 * moveAmt + Math.sin(time * 0.7) * 0.015 * idle;
    podPivot.rotation.x = -0.12 + Math.cos(phase * 0.5) * 0.02 * moveAmt;
    body.scale.y = 1 + Math.sin(time * 1.05) * 0.02 * idle;
    for (let i = 0; i < sprouts.length; i++) sprouts[i].rotation.y = Math.sin(time * 1.4 + i * 2.1) * 0.3;
  });
}

/**
 * Bone Hearse: black funeral carriage drawn by a skeletal horse — tall crooked cabin,
 * hanging funeral bell, pale-green soul lantern, bone filigree.
 */
function boneHearse(owner: 0 | 1, uiScale: number): FactionSquadRig {
  const root = new THREE.Group();
  const bone = mat(0xc9beaa, 0.94, 0.02);
  const boneDark = mat(0x9a8f7c, 0.95, 0);
  const lacquer = mat(mix(0x1e1a24, OWNER_COLORS[owner], 0.08), 0.62, 0.3);
  const lacquerDark = mat(0x120f16, 0.7, 0.25);
  const iron = mat(0x3a333b, 0.6, 0.5);
  const drape = mat(mix(0x33203a, OWNER_COLORS[owner], 0.14), 0.95, 0);
  const soul = glow(0x9fe8b5, 1.45);

  // ---- skeletal draft horse (forward, +z)
  const horse = new THREE.Group();
  horse.name = 'horse';
  horse.position.set(0, 0, 1.35);
  root.add(horse);
  const spineBody = new THREE.Mesh(new THREE.CapsuleGeometry(0.2, 0.8, 4, 7), bone);
  spineBody.name = 'body';
  spineBody.rotation.x = Math.PI / 2;
  spineBody.position.set(0, 1.05, 0);
  horse.add(spineBody);
  for (let i = 0; i < 4; i++) {
    const rib = new THREE.Mesh(new THREE.TorusGeometry(0.24, 0.03, 5, 9, Math.PI), bone);
    rib.rotation.set(Math.PI / 2, 0, Math.PI);
    rib.position.set(0, 1.02, 0.3 - i * 0.2);
    horse.add(rib);
  }
  const hipBone = new THREE.Mesh(new THREE.IcosahedronGeometry(0.2, 0), boneDark);
  hipBone.position.set(0, 1.08, -0.42);
  horse.add(hipBone);
  const horseLegs = [
    limb('hLegFL', bone, -0.16, 0.95, 0.9, 0.055),
    limb('hLegFR', bone, 0.16, 0.95, 0.9, 0.055),
    limb('hLegBL', bone, -0.16, 0.95, 0.9, 0.06),
    limb('hLegBR', bone, 0.16, 0.95, 0.9, 0.06),
  ];
  horseLegs[0].position.z = 0.42;
  horseLegs[1].position.z = 0.42;
  horseLegs[2].position.z = -0.42;
  horseLegs[3].position.z = -0.42;
  for (const leg of horseLegs) {
    const hoof = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.065, 0.1, 6), lacquerDark);
    hoof.position.y = -0.9;
    leg.add(hoof);
    horse.add(leg);
  }
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.14, 0.6, 6), bone);
  neck.position.set(0, 1.35, 0.5);
  neck.rotation.x = -0.7;
  horse.add(neck);
  const horseHead = new THREE.Group();
  horseHead.name = 'horseHead';
  horseHead.position.set(0, 1.62, 0.72);
  horse.add(horseHead);
  const skull = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.22, 0.3), bone);
  horseHead.add(skull);
  const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.16, 0.32), boneDark);
  muzzle.position.set(0, -0.06, 0.26);
  muzzle.rotation.x = 0.22;
  horseHead.add(muzzle);
  for (const sx of [-1, 1]) {
    const socket = new THREE.Mesh(new THREE.SphereGeometry(0.035, 5, 4), soul);
    socket.position.set(sx * 0.08, 0.04, 0.14);
    horseHead.add(socket);
    const earSpike = new THREE.Mesh(new THREE.ConeGeometry(0.03, 0.14, 4), boneDark);
    earSpike.position.set(sx * 0.08, 0.17, -0.04);
    horseHead.add(earSpike);
  }
  const plume = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.3, 5), drape);
  plume.position.set(0, 0.28, 0.02);
  horseHead.add(plume);
  const tailBone = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.012, 0.5, 4), bone);
  tailBone.position.set(0, 0.95, -0.6);
  tailBone.rotation.x = 0.75;
  horse.add(tailBone);
  // harness shafts back to the carriage
  for (const sx of [-1, 1]) {
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 1.5, 5), lacquerDark);
    shaft.rotation.x = Math.PI / 2;
    shaft.position.set(sx * 0.3, 0.85, -0.62);
    horse.add(shaft);
  }

  // ---- the hearse itself (behind, -z)
  const carriage = new THREE.Group();
  carriage.name = 'carriage';
  carriage.position.set(0, 0, -0.85);
  root.add(carriage);
  const bed = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.14, 1.7), lacquerDark);
  bed.position.y = 0.72;
  carriage.add(bed);
  const cabin = new THREE.Group();
  cabin.name = 'cabin';
  cabin.position.set(0, 1.5, -0.12);
  cabin.rotation.z = 0.045; // tall and slightly crooked
  carriage.add(cabin);
  const cab = new THREE.Mesh(new THREE.BoxGeometry(0.88, 1.35, 1.15), lacquer);
  cabin.add(cab);
  const casketWindow = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.4, 0.62), lacquerDark);
  casketWindow.position.set(0, 0.22, 0.1);
  cabin.add(casketWindow);
  const roof = new THREE.Mesh(new THREE.ConeGeometry(0.85, 0.75, 4), lacquerDark);
  roof.position.y = 1.02;
  roof.rotation.y = Math.PI / 4;
  roof.rotation.z = 0.06;
  cabin.add(roof);
  const finial = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.34, 4), bone);
  finial.position.set(0.06, 1.5, 0);
  cabin.add(finial);
  // bone filigree: ivory ribs tracing the cabin edges + corner spurs
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const ribTrim = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.038, 1.4, 5), bone);
      ribTrim.position.set(sx * 0.45, 0, sz * 0.58);
      cabin.add(ribTrim);
      const spur = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.26, 5), bone);
      spur.position.set(sx * 0.45, 0.78, sz * 0.58);
      spur.rotation.z = sx * -0.55;
      cabin.add(spur);
    }
    const scroll = new THREE.Mesh(new THREE.TorusGeometry(0.16, 0.028, 5, 10, Math.PI * 1.4), bone);
    scroll.position.set(sx * 0.47, -0.35, 0.42);
    scroll.rotation.y = Math.PI / 2;
    cabin.add(scroll);
  }
  // hanging funeral bell under a gallows arch on the cabin front
  const arch = new THREE.Mesh(new THREE.TorusGeometry(0.22, 0.032, 5, 10, Math.PI), iron);
  arch.position.set(0, 2.62, 0.55);
  carriage.add(arch);
  const bell = new THREE.Group();
  bell.name = 'bell';
  bell.position.set(0, 2.6, 0.55);
  carriage.add(bell);
  const bellBody = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.16, 0.24, 8), boneDark);
  bellBody.position.y = -0.14;
  bell.add(bellBody);
  const clapper = new THREE.Mesh(new THREE.SphereGeometry(0.045, 5, 4), iron);
  clapper.position.y = -0.3;
  bell.add(clapper);
  // pale-green soul lantern swinging from a front post
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.035, 0.85, 5), iron);
  post.position.set(0.42, 1.2, 0.78);
  post.rotation.z = -0.2;
  carriage.add(post);
  const lantern = new THREE.Group();
  lantern.name = 'lantern';
  lantern.position.set(0.5, 1.58, 0.8);
  carriage.add(lantern);
  const lanternCage = new THREE.Mesh(new THREE.TorusGeometry(0.1, 0.02, 5, 8), iron);
  lanternCage.position.y = -0.18;
  lantern.add(lanternCage);
  const soulFlame = new THREE.Mesh(new THREE.IcosahedronGeometry(0.085, 0), soul);
  soulFlame.position.y = -0.18;
  lantern.add(soulFlame);
  // funeral drapes on the flanks
  for (const sx of [-1, 1]) {
    const cloth = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 0.55), drape);
    cloth.position.set(sx * 0.55, 1.05, -0.12);
    cloth.rotation.y = sx * Math.PI / 2;
    carriage.add(cloth);
  }
  // wheels: tall rear pair, smaller front pair
  const wheels: THREE.Mesh[] = [];
  const wheelSpecs: Array<[number, number, number]> = [
    [-0.58, 0.42, 0.55], [0.58, 0.42, 0.55],
    [-0.58, 0.52, -0.62], [0.58, 0.52, -0.62],
  ];
  for (let i = 0; i < 4; i++) {
    const [wx, wr, wz] = wheelSpecs[i];
    const wheelGeo = new THREE.CylinderGeometry(wr, wr, 0.09, 10);
    wheelGeo.rotateZ(Math.PI / 2);
    const wheel = new THREE.Mesh(wheelGeo, lacquerDark);
    wheel.name = `wheel${i}`;
    wheel.position.set(wx, wr, wz);
    carriage.add(wheel);
    const hub = new THREE.Mesh(new THREE.SphereGeometry(0.06, 5, 4), bone);
    hub.position.set(wx * 1.08, wr, wz);
    carriage.add(hub);
    wheels.push(wheel);
  }

  return wrapAnnexRig(root, uiScale, 1.25, false, ({ time, phase, moveAmt, strike }) => {
    const stride = Math.sin(phase) * (0.03 + moveAmt * 0.5);
    horseLegs[0].rotation.x = stride;
    horseLegs[3].rotation.x = stride;
    horseLegs[1].rotation.x = -stride;
    horseLegs[2].rotation.x = -stride;
    for (const wheel of wheels) wheel.rotation.x = phase * 0.85;
    const idle = 1 - moveAmt;
    horseHead.rotation.x = 0.12 + Math.sin(time * 0.8) * 0.05 * idle + strike * 0.2;
    horseHead.rotation.y = Math.sin(time * 0.5) * 0.1 * idle;
    // the bell swings with the road and tolls harder at speed
    bell.rotation.x = Math.sin(time * (1.4 + moveAmt * 2.6)) * (0.06 + moveAmt * 0.3);
    bell.rotation.z = Math.cos(time * 1.1) * 0.05;
    lantern.rotation.x = Math.sin(time * 1.7 + 0.8) * (0.08 + moveAmt * 0.22);
    cabin.rotation.z = 0.045 + Math.sin(phase) * 0.018 * moveAmt + Math.sin(time * 0.7) * 0.008 * idle;
    soul.emissiveIntensity = 1.45 + Math.sin(time * 2.3) * 0.35 + strike * 1.0;
  });
}

/**
 * Prism Skiff: sleek hovering logistics craft — crystalline keel, twin outrigger prisms,
 * and a folded relay crystal glowing softly amidships. No legs; gentle hover bob.
 */
function prismSkiff(owner: 0 | 1, uiScale: number): FactionSquadRig {
  const root = new THREE.Group();
  const shell = mat(mix(0xdde6f2, OWNER_COLORS[owner], 0.15), 0.3, 0.68);
  const dark = mat(0x182038, 0.38, 0.74);
  const crystal = glow(0x43ceff, 1.35, 0.92);
  const relay = glow(0x7b61ff, 0.85, 0.95);

  // hull: elongated crystalline keel with a raised stern
  const hull = new THREE.Mesh(new THREE.OctahedronGeometry(0.62, 0), shell);
  hull.name = 'hull';
  hull.position.y = 1.0;
  hull.scale.set(0.85, 0.5, 2.1);
  root.add(hull);
  const deck = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.09, 1.7), dark);
  deck.position.y = 1.18;
  root.add(deck);
  const prow = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.75, 4), crystal);
  prow.rotation.x = Math.PI / 2;
  prow.rotation.z = Math.PI / 4;
  prow.position.set(0, 1.05, 1.45);
  root.add(prow);
  const stern = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.42, 0.32), shell);
  stern.position.set(0, 1.35, -0.85);
  stern.rotation.x = -0.25;
  root.add(stern);
  const keelFin = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.55, 4), shell);
  keelFin.position.set(0, 0.62, -0.4);
  keelFin.rotation.x = Math.PI;
  root.add(keelFin);

  // twin outrigger prisms on swept struts
  const outriggers: THREE.Mesh[] = [];
  for (const sx of [-1, 1]) {
    const strut = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.05, 0.85, 5), dark);
    strut.position.set(sx * 0.55, 1.05, 0.15);
    strut.rotation.z = sx * (Math.PI / 2 - 0.28);
    root.add(strut);
    const prism = new THREE.Mesh(new THREE.OctahedronGeometry(0.19, 0), crystal);
    prism.name = `outrigger${sx < 0 ? 'L' : 'R'}`;
    prism.position.set(sx * 0.95, 0.92, 0.15);
    prism.scale.set(1, 1.5, 1);
    root.add(prism);
    outriggers.push(prism);
  }

  // folded relay crystal cargo: a lashed-down cluster glowing softly amidships
  const cargo = new THREE.Group();
  cargo.name = 'cargo';
  cargo.position.set(0, 1.35, -0.15);
  root.add(cargo);
  const mainCrystal = new THREE.Mesh(new THREE.OctahedronGeometry(0.26, 0), relay);
  mainCrystal.rotation.z = 1.25; // folded flat on the deck
  mainCrystal.scale.set(1, 1.7, 1);
  cargo.add(mainCrystal);
  for (const [cx, cz, cr] of [[-0.16, 0.24, 0.13], [0.2, 0.1, 0.11], [0.02, -0.3, 0.12]] as const) {
    const shard = new THREE.Mesh(new THREE.OctahedronGeometry(cr, 0), relay);
    shard.position.set(cx, 0.02, cz);
    shard.rotation.set(cx * 4, cz * 3, 0.6);
    cargo.add(shard);
  }
  for (const rz of [0.18, -0.2]) {
    const lash = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.022, 4, 10, Math.PI), dark);
    lash.rotation.set(0, Math.PI / 2, 0);
    lash.position.set(0, -0.05, rz);
    cargo.add(lash);
  }

  return wrapAnnexRig(root, uiScale, 0.8, true, ({ time, moveAmt, strike }) => {
    // gentle hover bob; leans into travel like a barge under sail
    root.position.y = (0.5 + Math.sin(time * 1.75) * 0.07 + moveAmt * 0.14) * uiScale;
    root.rotation.x = 0.1 * moveAmt + Math.sin(time * 1.1) * 0.015;
    root.rotation.z = Math.sin(time * 0.7) * 0.03 * (1 - moveAmt * 0.6);
    for (let i = 0; i < outriggers.length; i++) {
      outriggers[i].rotation.y = time * (i % 2 ? 0.9 : -0.7);
    }
    cargo.rotation.y = Math.sin(time * 0.5) * 0.06;
    const pulse = 0.85 + Math.sin(time * 1.9) * 0.2 + strike * 0.8;
    relay.emissiveIntensity = pulse;
    crystal.emissiveIntensity = 1.35 + moveAmt * 0.5 + Math.sin(time * 2.4) * 0.2;
  });
}

function posePuppet(p: Puppet, variant: number, frame: FactionSquadPose): void {
  const { time, phase, moveAmt, strike, charge } = frame;
  const idle = 1 - moveAmt;
  let stride = Math.sin(phase) * moveAmt;
  if (p.gait === 'walk') stride *= 0.62 + charge * 0.22;
  else if (p.gait === 'crawl') stride *= 0.48 + charge * 0.38;
  else stride *= 0.2;
  if (p.legL) p.legL.rotation.x = stride;
  if (p.legR) p.legR.rotation.x = -stride;
  if (p.armL) p.armL.rotation.x = -stride * 0.55 - strike * 1.05;
  if (p.armR) p.armR.rotation.x = stride * 0.55 - strike * 0.55;
  if (p.weapon) {
    p.weapon.rotation.x = 0.48 - strike * 1.15;
    p.weapon.position.z = 0.15 + strike * 0.16;
  }
  if (p.body) {
    p.body.rotation.x = charge * 0.18 + strike * 0.22;
    p.body.rotation.z = Math.sin(phase) * 0.035 * moveAmt + Math.sin(time * 0.8 + variant) * 0.018 * idle;
    p.body.scale.y = 1 + Math.sin(time * 1.55 + variant) * 0.025 * idle - strike * 0.035;
  }
  if (p.head) {
    p.head.rotation.y = Math.sin(time * 0.58 + variant) * 0.12 * idle;
    p.head.rotation.x = charge * -0.12 + strike * 0.12;
  }
  const rigScale = p.root.scale.y;
  p.root.position.y = (p.baseY + (p.gait === 'hover' ? Math.sin(time * 1.8 + variant) * 0.08 : 0)) * rigScale;
  if (p.gait === 'serpent') {
    for (let i = 0; i < p.orbitals.length; i++) {
      const segment = p.orbitals[i];
      segment.position.x = Math.sin(time * 1.8 - i * 0.62 + phase * 0.12) * (0.1 + moveAmt * 0.15);
      segment.rotation.y = Math.cos(time * 1.8 - i * 0.62) * 0.2;
      segment.position.y = 0.32 + Math.sin(time * 2.2 - i * 0.7) * 0.16;
    }
  } else {
    for (let i = 0; i < p.orbitals.length; i++) {
      const orbital = p.orbitals[i];
      if (orbital.name === 'orbital') {
        const direction = i % 2 ? -1 : 1;
        orbital.rotation.x = time * (0.32 + i * 0.025) * direction + i * 0.45;
        orbital.rotation.y = time * (0.45 + i * 0.07) * direction;
        orbital.rotation.z = time * (0.22 + i * 0.02) + i * 0.3;
      } else if (p.gait === 'crawl') {
        orbital.rotation.x = Math.sin(phase + i * Math.PI * 0.5) * 0.52 * moveAmt;
      }
    }
  }
  for (let i = 0; i < p.pulseMeshes.length; i++) {
    const pulse = 1 + Math.sin(time * (2.1 + i * 0.2) + variant) * 0.08 + strike * 0.22;
    p.pulseMeshes[i].scale.setScalar(pulse);
    const material = p.pulseMeshes[i].material;
    if (material instanceof THREE.MeshStandardMaterial) material.emissiveIntensity = 1.15 + Math.sin(time * 2.4 + variant) * 0.2 + strike * 1.2;
  }
}

export function makeFactionSquadRig(defId: string, owner: 0 | 1, memberIndex: number, uiScale: number): FactionSquadRig {
  // Core Annex vehicles/beasts: single-member squads with dedicated pose closures
  if (defId === 'u-vw-rootwarden') return rootwardenTreant(owner, uiScale);
  if (defId === 'u-vw-seedback') return seedbackCarrier(owner, uiScale);
  if (defId === 'u-ah-bone-hearse') return boneHearse(owner, uiScale);
  if (defId === 'u-ad-prism-skiff') return prismSkiff(owner, uiScale);
  let puppet: Puppet;
  if (BESPOKE_ASHEN_SQUAD_DEFS.has(defId)) {
    puppet = defId === 'u-ah-ghoul-pack'
      ? ghoul(owner, memberIndex)
      : defId === 'u-ah-corpse-carrier' || defId === 'u-ah-gore-engine' || defId === 'u-ah-soul-lanterns' || defId === 'u-ah-grave-leviathan'
        ? ashenConstruct(defId, owner, memberIndex)
        : ashenHumanoid(defId, owner, memberIndex);
  } else if (BESPOKE_ASTRAL_SQUAD_DEFS.has(defId)) {
    puppet = defId === 'u-ad-phase-tank' || defId === 'u-ad-targeting-construct' || defId === 'u-ad-shield-drone' || defId === 'u-ad-siege-orb' || defId === 'u-ad-rift-hounds' || defId === 'u-ad-solar-ark'
      ? astralConstruct(defId, owner, memberIndex)
      : astralHumanoid(defId, owner, memberIndex);
  } else {
    throw new Error(`No bespoke faction squad rig for ${defId}`);
  }
  const shadowRadius = defId === 'u-ad-solar-ark' || defId === 'u-ah-grave-leviathan' ? 1.45 : defId === 'u-ah-gore-engine' || defId === 'u-ah-corpse-carrier' || defId === 'u-ad-phase-tank' ? 1.05 : 0.58;
  finish(puppet.root, uiScale, shadowRadius, puppet.gait === 'hover');
  // The puppet may bob internally, while the outer transform remains owned by the renderer's
  // terrain elevation and squad formation placement.
  const group = new THREE.Group();
  group.add(puppet.root);
  return {
    group,
    pose: (frame) => posePuppet(puppet, memberIndex, frame),
  };
}
